// Pure helpers for the customizable dashboards. A layout is an ordered list of [widgetId, columnSpan] pairs.
// It is persisted as { widgetOrder, hiddenWidgetKeys, widgetSizes }: visible widgets first (in order), then
// the removed ones listed in hiddenWidgetKeys so the server's "hidden must be in order" rule holds.

export const SIZE_LABELS = { 3: "1/4", 4: "1/3", 5: "5/12", 6: "1/2", 7: "7/12", 8: "2/3", 12: "Full" };

/** Default width for a widget added from the library: the registry's `add`, else half width if allowed, else its first size. */
export function addSize(def) {
  if (def.add) return def.add;
  return def.sizes.includes(6) ? 6 : def.sizes[0];
}

const validEntry = (registry, entry) => Array.isArray(entry) && registry[entry[0]] && registry[entry[0]].sizes.includes(entry[1]);

/** Layout from a saved server row; falls back to `defaults` when nothing usable was saved (including legacy rows). */
export function layoutFromSaved(saved, registry, defaults) {
  const order = Array.isArray(saved?.widgetOrder) ? saved.widgetOrder : null;
  if (!order) return defaults;
  const hidden = new Set(Array.isArray(saved.hiddenWidgetKeys) ? saved.hiddenWidgetKeys : []);
  const sizes = saved.widgetSizes && typeof saved.widgetSizes === "object" ? saved.widgetSizes : {};
  const defaultSize = Object.fromEntries(defaults);
  const entries = order
    .filter((id) => registry[id] && !hidden.has(id))
    .map((id) => {
      const size = sizes[id] ?? defaultSize[id] ?? addSize(registry[id]);
      return [id, registry[id].sizes.includes(size) ? size : addSize(registry[id])];
    });
  return entries.length || order.length === 0 ? entries : defaults;
}

/** Server payload for a layout. Every registry widget not on the dashboard is stored as hidden. */
export function layoutToSaved(layout, registry) {
  const visible = layout.map(([id]) => id);
  const removed = Object.keys(registry).filter((id) => !visible.includes(id));
  return {
    widgetOrder: [...visible, ...removed],
    hiddenWidgetKeys: removed,
    widgetSizes: Object.fromEntries(layout.map(([id, size]) => [id, size])),
  };
}

export const isValidLayout = (layout, registry) => Array.isArray(layout) && layout.every((entry) => validEntry(registry, entry));

export function moveEntry(layout, index, delta) {
  const target = index + delta;
  if (index < 0 || target < 0 || target >= layout.length) return layout;
  const next = [...layout];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** Move `sourceId` so it sits where `targetId` was. */
export function reorderEntry(layout, sourceId, targetId) {
  if (sourceId === targetId) return layout;
  const from = layout.find(([id]) => id === sourceId);
  const rest = layout.filter(([id]) => id !== sourceId);
  const at = rest.findIndex(([id]) => id === targetId);
  if (!from || at < 0) return layout;
  return [...rest.slice(0, at), from, ...rest.slice(at)];
}

export const resizeEntry = (layout, id, size) => layout.map((entry) => (entry[0] === id ? [id, size] : entry));
export const removeEntry = (layout, id) => layout.filter(([entryId]) => entryId !== id);
export const appendEntry = (layout, id, registry) => (layout.some(([entryId]) => entryId === id) ? layout : [...layout, [id, addSize(registry[id])]]);
